/**
 * Convert a mosaic tree into a react-grid-layout arrangement.
 *
 * Used when a layout first switches from 'mosaic' to 'grid' mode and has no
 * `gridLayout` yet: we seed the grid so the user's existing mosaic visually
 * carries over instead of collapsing to a blank stack.
 *
 * This is the ONLY direction that converts cleanly. Grid → mosaic is lossy
 * (arbitrary 2D positions don't map to a strict binary tree), so the store
 * preserves the original `mosaicTree` for the return trip rather than
 * regenerating it. See the design doc, §6.
 *
 * Model:
 *   - `row` splits → side-by-side cells. Width is divided by `splitPercentage`
 *     (the one dimension mosaic and grid share cleanly).
 *   - `column` splits → stacked cells, each a comfortable default height. Height
 *     comes from nesting depth, NOT from the split percentage — a research
 *     scroll wants every panel usably tall, and the page grows/scrolls to fit.
 *
 * All outputs are integers: x/w in grid columns, y/h in row units.
 */
import type { GridCell, MosaicNode } from '@/types/schemas/workspace'

/** Default column count. 24 (rather than RGL's usual 12) for finer horizontal steps. */
export const DEFAULT_GRID_COLS = 24

/**
 * Row units each leaf gets by default (≈ rowHeight × this = pixel height).
 * 16 rows × the 20px rowHeight ≈ 320px — the same default panel height as
 * before the grid resolution was doubled.
 */
export const DEFAULT_LEAF_H = 16

interface Laid {
  cells: GridCell[]
  /** Total height (row units) this subtree occupies. */
  height: number
}

function layout(node: MosaicNode, x: number, y: number, w: number): Laid {
  if (typeof node === 'string') {
    return { cells: [{ i: node, x, y, w, h: DEFAULT_LEAF_H }], height: DEFAULT_LEAF_H }
  }

  const pct = node.splitPercentage ?? 50

  if (node.direction === 'row') {
    // Side by side: share the width, sit at the same y.
    const firstW = Math.min(w - 1, Math.max(1, Math.round((w * pct) / 100)))
    const secondW = w - firstW
    const a = layout(node.first, x, y, firstW)
    const b = layout(node.second, x + firstW, y, secondW)
    return { cells: [...a.cells, ...b.cells], height: Math.max(a.height, b.height) }
  }

  // column — stack: full width each, second sits below the first.
  const a = layout(node.first, x, y, w)
  const b = layout(node.second, x, y + a.height, w)
  return { cells: [...a.cells, ...b.cells], height: a.height + b.height }
}

/**
 * Seed a grid arrangement from a mosaic tree. Returns [] for a null/empty tree.
 * A single leaf becomes one full-width cell.
 */
export function mosaicToGrid(
  tree: MosaicNode | null,
  cols: number = DEFAULT_GRID_COLS,
): GridCell[] {
  if (!tree) return []
  return layout(tree, 0, 0, cols).cells
}

/**
 * Reconcile a stored grid arrangement against the set of panels that actually
 * exist. Keeps existing cells (dropping any whose panel is gone), and appends a
 * full-width cell at the bottom for every panel that has no cell yet.
 *
 * This makes the grid self-healing: a panel added while in grid mode, or a
 * layout switched to grid before it was ever seeded, still renders — the new
 * panel drops in at the bottom instead of vanishing.
 */
// ─── grid → mosaic (guillotine slicing) ──────────────────────────────────

interface Bounds {
  minX: number
  minY: number
  maxX: number
  maxY: number
}

function boundsOf(cells: GridCell[]): Bounds {
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const c of cells) {
    minX = Math.min(minX, c.x)
    minY = Math.min(minY, c.y)
    maxX = Math.max(maxX, c.x + c.w)
    maxY = Math.max(maxY, c.y + c.h)
  }
  return { minX, minY, maxX, maxY }
}

interface Cut {
  direction: 'row' | 'column'
  first: GridCell[]
  second: GridCell[]
  splitPercentage: number
}

/**
 * Find a straight cut line (vertical → row split, horizontal → column split)
 * that cleanly partitions the cells into two non-empty groups without slicing
 * through any cell. Prefers vertical cuts. Returns null when no clean cut exists
 * (e.g. a pinwheel arrangement).
 */
function findCut(cells: GridCell[]): Cut | null {
  const b = boundsOf(cells)

  const xEdges = [...new Set(cells.flatMap((c) => [c.x, c.x + c.w]))]
    .sort((p, q) => p - q)
    .filter((x) => x > b.minX && x < b.maxX)
  for (const x of xEdges) {
    const first = cells.filter((c) => c.x + c.w <= x)
    const second = cells.filter((c) => c.x >= x)
    if (first.length && second.length && first.length + second.length === cells.length) {
      return { direction: 'row', first, second, splitPercentage: pct(x - b.minX, b.maxX - b.minX) }
    }
  }

  const yEdges = [...new Set(cells.flatMap((c) => [c.y, c.y + c.h]))]
    .sort((p, q) => p - q)
    .filter((y) => y > b.minY && y < b.maxY)
  for (const y of yEdges) {
    const first = cells.filter((c) => c.y + c.h <= y)
    const second = cells.filter((c) => c.y >= y)
    if (first.length && second.length && first.length + second.length === cells.length) {
      return {
        direction: 'column',
        first,
        second,
        splitPercentage: pct(y - b.minY, b.maxY - b.minY),
      }
    }
  }

  return null
}

function pct(part: number, whole: number): number {
  if (whole <= 0) return 50
  return Math.min(90, Math.max(10, Math.round((part / whole) * 100)))
}

/** Fallback for un-sliceable arrangements: stack in reading order (y, then x). */
function stackColumn(cells: GridCell[]): MosaicNode {
  const sorted = [...cells].sort((a, b) => a.y - b.y || a.x - b.x)
  let node: MosaicNode = sorted[sorted.length - 1]!.i
  for (let k = sorted.length - 2; k >= 0; k--) {
    node = { direction: 'column', first: sorted[k]!.i, second: node }
  }
  return node
}

function buildTree(cells: GridCell[]): MosaicNode {
  if (cells.length === 1) return cells[0]!.i
  const cut = findCut(cells)
  if (!cut) return stackColumn(cells)
  return {
    direction: cut.direction,
    first: buildTree(cut.first),
    second: buildTree(cut.second),
    splitPercentage: cut.splitPercentage,
  }
}

/**
 * Convert a grid arrangement into a mosaic tree via guillotine slicing —
 * recursively split the cells by a clean cut line, falling back to a vertical
 * stack when no clean cut exists. Lossy by nature (a 2-D grid isn't always a
 * binary tree), which is fine for a deliberate one-shot layout conversion.
 * Returns null for an empty grid.
 */
export function gridToMosaic(cells: GridCell[]): MosaicNode | null {
  if (!cells.length) return null
  return buildTree(cells)
}

export function reconcileGridCells(
  cells: GridCell[],
  panelIds: string[],
  cols: number = DEFAULT_GRID_COLS,
): GridCell[] {
  const present = new Set(panelIds)
  // Drop cells whose panel no longer exists.
  const kept = cells.filter((c) => present.has(c.i))
  const have = new Set(kept.map((c) => c.i))
  // Next free row = below the lowest occupied cell.
  let nextY = kept.reduce((max, c) => Math.max(max, c.y + c.h), 0)
  const appended: GridCell[] = []
  for (const id of panelIds) {
    if (have.has(id)) continue
    appended.push({ i: id, x: 0, y: nextY, w: cols, h: DEFAULT_LEAF_H })
    nextY += DEFAULT_LEAF_H
  }
  return appended.length ? [...kept, ...appended] : kept
}
